import { useMemo } from "react";

import { getSkyflowRecord, postSkyflowRecords, putSkyflowRecords } from "config/skyflow";

/**
 * Drop-in replacements for `skyflow-react-js` hooks that route all operations
 * through our backend proxy (Vaulto). No Skyflow bearer tokens in the browser.
 * Redaction is not sent to the backend in phase one (phase two later).
 *
 * Supported subset (based on current usage in this repo):
 * - `useSkyflow().get({ records: [{ table, ids: [id], column_name, column_values }] })`
 * - `useCollectContainer().collect({ additionalFields: { records: [{ table, fields }] }, tokens })`
 */

const normalizeRedaction = (redaction) => {
	// Kept for phase two when redaction may be sent again.
	if (!redaction) return undefined;
	if (typeof redaction === "string") return redaction;
	return String(redaction);
};

/** Notes/tasks list the same patient_id per row; dedupe before batch Skyflow filter. */
const dedupeSkyflowIds = (ids) => [...new Set((Array.isArray(ids) ? ids : [ids]).filter(Boolean).map(String))];

const normalizeSkyflowFields = (fields = {}, fallbackId) => {
	const safe = fields && typeof fields === "object" ? fields : {};
	// Many call sites assume `id` is the Skyflow record id. With the proxy, we usually get `skyflow_id`.
	// Preserve an existing `id`, otherwise derive it from `skyflow_id` or the requested id.
	const derivedId = safe?.id || safe?.skyflow_id || fallbackId || "";
	return derivedId && !safe?.id ? { ...safe, id: derivedId } : safe;
};

export const useSkyflow = () =>
	useMemo(
		() => ({
			get: async ({ records = [] } = {}) => {
				const out = [];

				for (const recordReq of records || []) {
					const table = recordReq?.table;

					// `ids` path (many call sites pass multiple ids; we must return them all)
					if (Array.isArray(recordReq?.ids) && recordReq.ids.length) {
						const ids = dedupeSkyflowIds(recordReq.ids);
						if (!ids.length) {
							out.push({ fields: {} });
							continue;
						}
						// Fast path: use column filter to fetch many rows in one request.
						// Backend proxy supports `column_name` + `column_values`.
						if (ids.length > 1) {
							const res = await getSkyflowRecord({
								table,
								column_name: "skyflow_id",
								column_values: ids,
								redaction: "PLAIN_TEXT",
							});

							if (Array.isArray(res?.records) && res.records.length) {
								out.push(
									...res.records.map((r) => ({
										fields: normalizeSkyflowFields(r?.fields || {}),
									}))
								);
							} else {
								// Fallback if backend returns single shape
								const fields = res?.fields || res?.records?.[0]?.fields || {};
								out.push({ fields: normalizeSkyflowFields(fields) });
							}
							continue;
						}

						// Single id path
						const res = await getSkyflowRecord({
							table,
							id: ids[0],
							redaction: "PLAIN_TEXT",
						});
						const fields = res?.fields || res?.records?.[0]?.fields || {};
						out.push({ fields: normalizeSkyflowFields(fields, ids[0]) });
						continue;
					}

					// `column_name` + `column_values` path
					if (recordReq?.column_name && recordReq?.column_values) {
						const columnValues =
							recordReq.column_name === "skyflow_id"
								? dedupeSkyflowIds(recordReq.column_values)
								: recordReq.column_values;
						const res = await getSkyflowRecord({
							table,
							column_name: recordReq.column_name,
							column_values: columnValues,
							redaction: "PLAIN_TEXT",
						});

						// Some Skyflow responses return many records.
						if (Array.isArray(res?.records) && res.records.length) {
							out.push(
								...res.records.map((r) => ({
									fields: normalizeSkyflowFields(r?.fields || {}),
								}))
							);
						} else {
							const fields = res?.fields || res?.records?.[0]?.fields || {};
							out.push({ fields: normalizeSkyflowFields(fields) });
						}
						continue;
					}

					out.push({ fields: {} });
				}

				return { records: out };
			},
		}),
		[]
	);

export const useCollectContainer = () =>
	useMemo(
		() => ({
			collect: async ({ additionalFields = {}, tokens } = {}) => {
				const records = additionalFields?.records || [];
				if (!Array.isArray(records) || records.length === 0) {
					return { records: [] };
				}

				const normalizeUpdateRecord = (r) => {
					const fields = r?.fields && typeof r.fields === "object" ? r.fields : {};
					const id =
						fields?.skyflowID ||
						fields?.skyflow_id ||
						fields?.id ||
						r?.skyflowID ||
						r?.skyflow_id ||
						r?.id ||
						null;
					if (!id) return null;
					const { skyflowID, skyflow_id, id: _id, ...rest } = fields || {};
					return { id: String(id), fields: rest || {} };
				};

				// Group by table to minimize requests, and split inserts vs updates.
				const byTable = records.reduce((acc, r) => {
					const table = r?.table;
					if (!table) return acc;
					if (!acc[table]) acc[table] = { inserts: [], updates: [] };

					const updateRecord = normalizeUpdateRecord(r);
					if (updateRecord) {
						acc[table].updates.push(updateRecord);
					} else {
						acc[table].inserts.push({ fields: r?.fields || {} });
					}

					return acc;
				}, {});

				const results = [];
				for (const [table, tableOps] of Object.entries(byTable)) {
					// Inserts
					if (Array.isArray(tableOps?.inserts) && tableOps.inserts.length) {
						// `tokens` flag is ignored by the proxy; Skyflow side decides based on policy.
						const res = await postSkyflowRecords({
							table,
							body: JSON.stringify({
								...(tokens === false ? { tokens: false } : {}),
								records: tableOps.inserts,
							}),
						});
						if (Array.isArray(res?.records)) results.push(...res.records);
					}

					// Updates
					if (Array.isArray(tableOps?.updates) && tableOps.updates.length) {
						const res = await putSkyflowRecords({
							table,
							body: JSON.stringify({
								records: tableOps.updates,
							}),
						});
						if (Array.isArray(res?.records)) results.push(...res.records);
					}
				}

				return { records: results };
			},
		}),
		[]
	);

/**
 * Non-hook variant for places that are not React components/hooks (e.g. axios interceptors).
 * Calling React hooks (like `useCollectContainer`) there will violate `react-hooks/rules-of-hooks`.
 */
export const createCollectContainer = () => ({
	collect: async ({ additionalFields = {}, tokens } = {}) => {
		const records = additionalFields?.records || [];
		if (!Array.isArray(records) || records.length === 0) {
			return { records: [] };
		}

		const normalizeUpdateRecord = (r) => {
			const fields = r?.fields && typeof r.fields === "object" ? r.fields : {};
			const id =
				fields?.skyflowID || fields?.skyflow_id || fields?.id || r?.skyflowID || r?.skyflow_id || r?.id || null;
			if (!id) return null;
			const { skyflowID, skyflow_id, id: _id, ...rest } = fields || {};
			return { id: String(id), fields: rest || {} };
		};

		// Group by table to minimize requests, and split inserts vs updates.
		const byTable = records.reduce((acc, r) => {
			const table = r?.table;
			if (!table) return acc;
			if (!acc[table]) acc[table] = { inserts: [], updates: [] };

			const updateRecord = normalizeUpdateRecord(r);
			if (updateRecord) {
				acc[table].updates.push(updateRecord);
			} else {
				acc[table].inserts.push({ fields: r?.fields || {} });
			}
			return acc;
		}, {});

		const results = [];
		for (const [table, tableOps] of Object.entries(byTable)) {
			// Inserts
			if (Array.isArray(tableOps?.inserts) && tableOps.inserts.length) {
				// `tokens` flag is ignored by the proxy; Skyflow side decides based on policy.
				const res = await postSkyflowRecords({
					table,
					body: JSON.stringify({
						...(tokens === false ? { tokens: false } : {}),
						records: tableOps.inserts,
					}),
				});
				if (Array.isArray(res?.records)) results.push(...res.records);
			}

			// Updates
			if (Array.isArray(tableOps?.updates) && tableOps.updates.length) {
				const res = await putSkyflowRecords({
					table,
					body: JSON.stringify({
						records: tableOps.updates,
					}),
				});
				if (Array.isArray(res?.records)) results.push(...res.records);
			}
		}

		return { records: results };
	},
});
